import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { find } from 'lodash/fp';
import * as Yup from 'yup';

import { api, getApiUrl, PaginatedList, ResponseError } from 'utils/api';
import { NoticeType, AttachmentResponse, NoticeListResponse } from 'utils/api/notice';
import { noticeAdminApi } from 'utils/api/notices';
import { noticeTypes, notificationTypes } from 'utils/constants';
import { LanguagesList } from 'utils/api/users';
import { useApp } from 'App';
import { ActionTypes } from 'App/types';
import usePrefix from 'utils/usePrefix';

import Modal from 'components/Modal';
import ConfirmModal from 'components/ConfirmModal';

import Form from './Form';
import Target from './Target';

const validationSchema = (ty: (key: string, vars?: object) => string) =>
  Yup.object().shape({
    title: Yup.string().required(ty('field_required')),
    name: Yup.string().required(ty('field_required')),
    content: Yup.string().required(ty('field_required')),
  });

const url = getApiUrl('/notices/type');

interface Props {
  notices: NoticeListResponse[];
  noticeId: number | undefined;
  onClose: () => void;
  fetchData: () => Promise<PaginatedList<NoticeListResponse[]> | void>;
  isOpen: boolean;
}

export interface FormValues {
  type: NoticeType;
  language?: LanguagesList;
  title: string;
  name: string;
  content: string;
  violenceId?: number;
  attachments?: AttachmentResponse[];
  sent_to_schools?: number[];
  universally_available: boolean;
}

const Notice: React.FC<Props> = ({ notices, noticeId, onClose, isOpen, fetchData }) => {
  const [, dispatch] = useApp();

  const ty = usePrefix('YupErrors');
  const tn = usePrefix('Notices');

  const [refetchDetails, setRefetchDetails] = useState(false);
  const [noticeDetails, setNoticeDetails] = useState<FormValues>();
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
  const dirtyRef = useRef(false);

  const handleModalClose = () => {
    if (dirtyRef.current) {
      setIsUnsavedChangesOpen(true);
    } else {
      onClose();
    }
  };
  const initialValues: FormValues = {
    type: noticeTypes.advice,
    language: undefined,
    title: '',
    name: '',
    content: '',
    universally_available: false
  };

  const handleRefetchDetails = (val: boolean) => setRefetchDetails(val);

  const getNotice = (id?: number) => find((notice) => notice.id === id, notices);

  const loadData = async () => {
    const notice = getNotice(noticeId);
    if (!notice) return;
    try {
      const response = await api(`${url}/${notice.type}/${noticeId}`, {
        method: 'GET',
      });

      if (response.details) {
        setNoticeDetails({
          ...response.details,
          // eslint-disable-next-line no-magic-numbers
          violenceId: response.violenceId || 0,
          type: notice.type,
          sent_to_schools: response.sent_to_schools,
        });
      }
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  useEffect(() => {
    if (noticeId) {
      loadData();
      return;
    }
    setNoticeDetails(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refetchDetails) return;
    loadData();
    handleRefetchDetails(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchDetails]);

  const submit = async (values: FormValues) => {
    const { attachments, type, ...rest } = values;
    const attachmentIds = (attachments ?? [])
      .map((a) => a.id)
      .filter((id): id is number => typeof id === 'number');
    const payload = { ...rest, attachment_ids: attachmentIds };
    try {
      const response = await api(
        !noticeId ? `${url}/${type}` : `${url}/${type}/${noticeId}`,
        {
          method: noticeId ? 'PUT' : 'POST',
          payload,
        }
      );

      if (response) {
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { type: notificationTypes.success },
        });
        fetchData();
        onClose();
      }
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  const deleteNotice = async () => {
    if (!noticeId) return;
    try {
      await noticeAdminApi.deleteNotice(noticeId);
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.success },
      });
      await fetchData();
      onClose();
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  if (!noticeDetails) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} paddingTop="24px">
      <Formik
        initialValues={noticeDetails}
        onSubmit={submit}
        enableReinitialize={true}
        validationSchema={() => validationSchema(ty)}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue, dirty, submitForm }) => {
          dirtyRef.current = dirty;
          return (
            <>
              <Form
                errors={errors}
                touched={touched}
                isSubmitting={isSubmitting}
                onClose={onClose}
                values={values}
                setFieldValue={setFieldValue}
                noticeId={noticeId}
                handleRefetchDetails={handleRefetchDetails}
                onDelete={noticeId ? deleteNotice : undefined}
              />
              {!!noticeId && (
                <Target
                  noticeDetails={noticeDetails}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              )}
              {isUnsavedChangesOpen && (
                <ConfirmModal
                  title={tn('unsaved_changes_title')}
                  text={tn('unsaved_changes_text')}
                  confirmLabel={tn('save_and_exit')}
                  onCancel={() => setIsUnsavedChangesOpen(false)}
                  onConfirm={async () => {
                    setIsUnsavedChangesOpen(false);
                    await submitForm();
                  }}
                />
              )}
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default Notice;
